import { serializeTenantQueryArgs } from '@core/api/tenantContext';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api/baseQuery';

const dataroomUrl = (projectUuid, path) =>
  projectUuid ? `/api/projects/${projectUuid}/${path}` : `/api/projects/general/${path}`;

/**
 * Dataroom API using RTK Query
 */
export const dataroomApi = createApi({
  serializeQueryArgs: serializeTenantQueryArgs,
  reducerPath: 'dataroomApi',
  baseQuery,
  tagTypes: ['Folder', 'Document', 'DocumentVersion'],
  endpoints: (builder) => ({
    getFolders: builder.query({
      query: (projectUuid) => dataroomUrl(projectUuid, 'folders/'),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'Folder', id: uuid })),
              { type: 'Folder', id: 'LIST' },
            ]
          : [{ type: 'Folder', id: 'LIST' }],
    }),

    createFolder: builder.mutation({
      query: ({ projectUuid, name, parentUuid }) => ({
        url: dataroomUrl(projectUuid, 'folders/'),
        method: 'POST',
        body: { name, parent_uuid: parentUuid || null },
      }),
      invalidatesTags: [{ type: 'Folder', id: 'LIST' }],
    }),

    renameFolder: builder.mutation({
      query: ({ projectUuid, folderUuid, name }) => ({
        url: dataroomUrl(projectUuid, `folders/${folderUuid}/`),
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: (result, error, { folderUuid }) => [
        { type: 'Folder', id: folderUuid },
        { type: 'Folder', id: 'LIST' },
      ],
    }),

    deleteFolder: builder.mutation({
      query: ({ projectUuid, folderUuid }) => ({
        url: dataroomUrl(projectUuid, `folders/${folderUuid}/`),
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Folder', id: 'LIST' },
        { type: 'Document', id: 'LIST' },
      ],
    }),

    getDocuments: builder.query({
      query: ({ projectUuid, folderUuid }) => {
        const base = dataroomUrl(projectUuid, 'documents/');
        if (folderUuid) {
          return `${base}?folder_uuid=${folderUuid}`;
        }
        return `${base}?root_only=true`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({ type: 'Document', id: uuid })),
              { type: 'Document', id: 'LIST' },
            ]
          : [{ type: 'Document', id: 'LIST' }],
    }),

    uploadDocuments: builder.mutation({
      query: ({ projectUuid, folderUuid, files, folderPaths }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        if (folderUuid) {
          formData.append('folder_uuid', folderUuid);
        }
        if (folderPaths) {
          folderPaths.forEach((path) => formData.append('folder_paths', path));
        }
        return {
          url: dataroomUrl(projectUuid, 'documents/'),
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [
        { type: 'Document', id: 'LIST' },
        { type: 'Folder', id: 'LIST' },
      ],
    }),

    renameDocument: builder.mutation({
      query: ({ projectUuid, documentUuid, name }) => ({
        url: dataroomUrl(projectUuid, `documents/${documentUuid}/`),
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: (result, error, { documentUuid }) => [
        { type: 'Document', id: documentUuid },
        { type: 'Document', id: 'LIST' },
      ],
    }),

    moveDocument: builder.mutation({
      query: ({ projectUuid, documentUuid, folderUuid }) => ({
        url: dataroomUrl(projectUuid, `documents/${documentUuid}/`),
        method: 'PATCH',
        body: { folder: folderUuid },
      }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),

    duplicateDocument: builder.mutation({
      query: ({ projectUuid, documentUuid }) => ({
        url: dataroomUrl(projectUuid, `documents/${documentUuid}/duplicate/`),
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),

    deleteDocument: builder.mutation({
      query: ({ projectUuid, documentUuid }) => ({
        url: dataroomUrl(projectUuid, `documents/${documentUuid}/`),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { documentUuid }) => [
        { type: 'Document', id: documentUuid },
        { type: 'Document', id: 'LIST' },
      ],
    }),

    getPreviewUrl: builder.query({
      query: ({ projectUuid, documentUuid }) =>
        dataroomUrl(projectUuid, `documents/${documentUuid}/preview-url/`),
    }),

    getMarkdownUrl: builder.query({
      query: ({ projectUuid, documentUuid }) =>
        dataroomUrl(projectUuid, `documents/${documentUuid}/markdown-url/`),
    }),

    getDownloadUrl: builder.query({
      query: ({ projectUuid, documentUuid }) =>
        dataroomUrl(projectUuid, `documents/${documentUuid}/download-url/`),
    }),

    getWopiToken: builder.query({
      query: ({ projectUuid, documentUuid, editable }) => {
        const params = editable ? '?editable=true' : '';
        return `${dataroomUrl(projectUuid, `documents/${documentUuid}/wopi-token/`)}${params}`;
      },
    }),

    getDocumentVersions: builder.query({
      query: ({ projectUuid, documentUuid }) =>
        dataroomUrl(projectUuid, `documents/${documentUuid}/versions/`),
      providesTags: (result, error, { documentUuid }) => [
        { type: 'DocumentVersion', id: documentUuid },
      ],
    }),

    getVersionPreviewUrl: builder.query({
      query: ({ projectUuid, documentUuid, versionId }) =>
        dataroomUrl(projectUuid, `documents/${documentUuid}/versions/${versionId}/preview-url/`),
    }),

    getVersionDiff: builder.query({
      query: ({ projectUuid, documentUuid, versionId }) =>
        dataroomUrl(projectUuid, `documents/${documentUuid}/versions/${versionId}/diff/`),
      transformResponse: (response) => ({
        oldValue: response.old_value,
        newValue: response.new_value,
        fromVersion: response.version_number,
        toVersion: response.current_version_number,
      }),
    }),

    restoreVersion: builder.mutation({
      query: ({ projectUuid, documentUuid, versionId }) => ({
        url: dataroomUrl(projectUuid, `documents/${documentUuid}/versions/${versionId}/restore/`),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { documentUuid }) => [
        { type: 'Document', id: documentUuid },
        { type: 'DocumentVersion', id: documentUuid },
      ],
    }),

    saveAgentFile: builder.mutation({
      query: ({ projectUuid, sourceUrl, filename }) => ({
        url: dataroomUrl(projectUuid, 'documents/from-agent/'),
        method: 'POST',
        body: { source_url: sourceUrl, filename },
      }),
      invalidatesTags: [{ type: 'Document', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetFoldersQuery,
  useCreateFolderMutation,
  useRenameFolderMutation,
  useDeleteFolderMutation,
  useGetDocumentsQuery,
  useUploadDocumentsMutation,
  useRenameDocumentMutation,
  useMoveDocumentMutation,
  useDuplicateDocumentMutation,
  useDeleteDocumentMutation,
  useLazyGetPreviewUrlQuery,
  useLazyGetMarkdownUrlQuery,
  useLazyGetDownloadUrlQuery,
  useLazyGetWopiTokenQuery,
  useGetDocumentVersionsQuery,
  useLazyGetVersionPreviewUrlQuery,
  useLazyGetVersionDiffQuery,
  useRestoreVersionMutation,
  useSaveAgentFileMutation,
} = dataroomApi;
